/**
 * Vector Store Service
 *
 * Client for interacting with Supabase pgvector storage.
 * Handles document chunk storage and similarity search.
 */

import { supabase } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import type { Json } from '@/lib/supabase/types';
import type { DDSource } from './assistant.types';
import { reportError } from '@/lib/errors';
import { insertNotification } from '@/lib/notifications/client';

// ============================================
// Types
// ============================================

export interface DocumentChunkRow {
  id: string;
  document_id: string;
  startup_id: string;
  chunk_index: number;
  chunk_text: string;
  embedding: string | null;
  metadata: ChunkMetadata;
  created_at: string;
}

export interface ChunkMetadata {
  filename: string;
  documentType: string;
  pageNumber?: number;
  sectionType?: string;
  sectionTitle?: string;
  tokenCount: number;
}

export interface ConversationRow {
  id: string;
  investor_user_id: string;
  startup_id: string;
  summary: string | null;
  topics: string[];
  message_count: number;
  total_tokens: number;
  created_at: string;
  last_message_at: string;
}

export interface ConversationMessageRow {
  id: string;
  conversation_id: string;
  investor_user_id: string;
  startup_id: string;
  role: 'user' | 'assistant';
  content: string;
  embedding: string | null;
  tokens: number;
  created_at: string;
}

export interface SearchResult {
  id: string;
  document_id: string;
  chunk_index: number;
  chunk_text: string;
  metadata: ChunkMetadata;
  similarity: number;
}

export interface HistorySearchResult {
  id: string;
  conversation_id: string;
  role: string;
  content: string;
  created_at: string;
  similarity: number;
}

export interface HybridSearchResult extends SearchResult {
  semantic_score: number;
  keyword_score: number;
  combined_score: number;
}

// ============================================
// Document Chunks
// ============================================

/**
 * Store document chunks with embeddings.
 */
export async function storeDocumentChunks(
  chunks: Array<{
    documentId: string;
    startupId: string;
    chunkIndex: number;
    chunkText: string;
    embedding: number[];
    metadata: ChunkMetadata;
  }>
): Promise<{ success: boolean; count: number; error?: string }> {
  try {
    const rows = chunks.map((chunk) => ({
      document_id: chunk.documentId,
      startup_id: chunk.startupId,
      chunk_index: chunk.chunkIndex,
      chunk_text: chunk.chunkText,
      embedding: `[${chunk.embedding.join(',')}]`,
      metadata: chunk.metadata as unknown as Json,
    }));

    const { error } = await supabase.from('document_chunks').upsert(rows, {
      onConflict: 'document_id,chunk_index',
    });

    if (error) throw error;

    return { success: true, count: chunks.length };
  } catch (err) {
    return {
      success: false,
      count: 0,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Delete document chunks for a document.
 */
export async function deleteDocumentChunks(
  documentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('document_chunks')
      .delete()
      .eq('document_id', documentId);

    if (error) throw error;

    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Search document chunks by semantic similarity.
 */
export async function searchDocumentChunks(
  queryEmbedding: number[],
  startupId: string,
  options: {
    matchCount?: number;
    similarityThreshold?: number;
  } = {}
): Promise<SearchResult[]> {
  const { matchCount = 8, similarityThreshold = 0.7 } = options;

  const { data, error } = await rpc('match_document_chunks', {
    query_embedding: `[${queryEmbedding.join(',')}]`,
    p_startup_id: startupId,
    match_count: matchCount,
    similarity_threshold: similarityThreshold,
  });

  if (error) {
    reportError(error, { context: 'VectorStore.searchDocuments', level: 'warning' });
    return [];
  }

  // RPC returns metadata as Json; narrow to ChunkMetadata (the stored shape)
  return (data || []) as unknown as SearchResult[];
}

/**
 * Hybrid search combining semantic and keyword matching.
 */
export async function hybridSearchDocuments(
  queryEmbedding: number[],
  queryText: string,
  startupId: string,
  options: {
    matchCount?: number;
    semanticWeight?: number;
  } = {}
): Promise<HybridSearchResult[]> {
  const { matchCount = 8, semanticWeight = 0.7 } = options;

  const { data, error } = await rpc('hybrid_search_documents', {
    query_embedding: `[${queryEmbedding.join(',')}]`,
    query_text: queryText,
    p_startup_id: startupId,
    match_count: matchCount,
    semantic_weight: semanticWeight,
  });

  if (error) {
    reportError(error, { context: 'VectorStore.hybridSearchDocuments', level: 'warning' });
    return [];
  }

  // RPC returns metadata as Json; narrow to ChunkMetadata (the stored shape)
  return (data || []) as unknown as HybridSearchResult[];
}

// ============================================
// Conversation History
// ============================================

/**
 * Create a new conversation.
 */
export async function createConversation(
  investorId: string,
  startupId: string
): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from('dd_conversations')
    .insert({
      investor_user_id: investorId,
      startup_id: startupId,
    })
    .select('id')
    .single();

  if (error) {
    reportError(error, { context: 'VectorStore.createConversation', level: 'warning' });
    return null;
  }

  return data;
}

/**
 * Get conversation summaries for an investor-startup pair.
 */
export async function getConversations(
  investorId: string,
  startupId: string,
  limit: number = 10
): Promise<ConversationRow[]> {
  const { data, error } = await supabase
    .from('dd_conversations')
    .select('*')
    .eq('investor_user_id', investorId)
    .eq('startup_id', startupId)
    .order('last_message_at', { ascending: false })
    .limit(limit);

  if (error) {
    reportError(error, { context: 'VectorStore.getConversations', level: 'warning' });
    return [];
  }

  return (data || []) as unknown as ConversationRow[];
}

/**
 * Store a conversation message with embedding.
 */
export async function storeConversationMessage(
  conversationId: string,
  investorId: string,
  startupId: string,
  role: 'user' | 'assistant',
  content: string,
  embedding: number[] | null,
  tokens: number
): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from('dd_conversation_messages')
    .insert({
      conversation_id: conversationId,
      investor_user_id: investorId,
      startup_id: startupId,
      role,
      content,
      embedding: embedding ? `[${embedding.join(',')}]` : null,
      tokens,
    })
    .select('id')
    .single();

  if (error) {
    reportError(error, { context: 'VectorStore.storeConversationMessage', level: 'warning' });
    return null;
  }

  // Notify startup owner when an investor sends a message (not for assistant replies)
  if (role === 'user') {
    const { data: sp } = await supabase
      .from('startup_profiles')
      .select('user_id')
      .eq('id', startupId)
      .maybeSingle();

    if (sp?.user_id) {
      insertNotification({
        userId: sp.user_id,
        type: 'message',
        title: 'New due diligence message',
        body: content.length > 80 ? content.slice(0, 80) + '...' : content,
        data: { conversationId, investorId },
      });
    }
  }

  return data;
}

/**
 * Search conversation history by semantic similarity.
 */
export async function searchConversationHistory(
  queryEmbedding: number[],
  investorId: string,
  startupId: string,
  options: {
    matchCount?: number;
    similarityThreshold?: number;
  } = {}
): Promise<HistorySearchResult[]> {
  const { matchCount = 5, similarityThreshold = 0.6 } = options;

  const { data, error } = await rpc('match_conversation_history', {
    query_embedding: `[${queryEmbedding.join(',')}]`,
    p_investor_id: investorId,
    p_startup_id: startupId,
    match_count: matchCount,
    similarity_threshold: similarityThreshold,
  });

  if (error) {
    reportError(error, { context: 'VectorStore.searchConversationHistory', level: 'warning' });
    return [];
  }

  return (data || []) as HistorySearchResult[];
}

/**
 * Update conversation summary.
 */
export async function updateConversationSummary(
  conversationId: string,
  summary: string,
  topics: string[]
): Promise<boolean> {
  const { error } = await supabase
    .from('dd_conversations')
    .update({ summary, topics })
    .eq('id', conversationId);

  if (error) {
    reportError(error, { context: 'VectorStore.updateConversationSummary', level: 'warning' });
    return false;
  }

  return true;
}

/**
 * Delete conversation and all messages.
 */
export async function deleteConversation(
  conversationId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('dd_conversations')
    .delete()
    .eq('id', conversationId);

  if (error) {
    reportError(error, { context: 'VectorStore.deleteConversation', level: 'warning' });
    return false;
  }

  return true;
}

// ============================================
// Context Assembly
// ============================================

/**
 * Convert search results to DDSource format for UI display.
 */
export function searchResultsToSources(
  results: SearchResult[],
  type: 'document' | 'history' = 'document'
): DDSource[] {
  return results.map((result) => ({
    type,
    label: result.metadata?.filename || 'Unknown',
    snippet: truncateText(result.chunk_text, 150),
    referenceId: result.document_id,
    location: result.metadata?.pageNumber
      ? `Page ${result.metadata.pageNumber}`
      : result.metadata?.sectionTitle || undefined,
    relevance: result.similarity,
  }));
}

/**
 * Assemble context from search results within token budget.
 */
export function assembleContext(
  documentResults: SearchResult[],
  historyResults: HistorySearchResult[],
  maxTokens: number = 4000
): {
  documentContext: string;
  historyContext: string;
  sources: DDSource[];
  totalTokens: number;
} {
  const sources: DDSource[] = [];
  let documentContext = '';
  let historyContext = '';
  let totalTokens = 0;

  // Add document chunks (prioritize by relevance)
  const sortedDocs = [...documentResults].sort(
    (a, b) => b.similarity - a.similarity
  );

  for (const doc of sortedDocs) {
    const chunkTokens = estimateTokens(doc.chunk_text);
    // B30: always admit the single most-relevant chunk (documentContext is still
    // empty on the first iteration), even if it alone exceeds the budget. Otherwise a
    // top chunk larger than maxTokens*0.7 broke the loop immediately, yielding EMPTY
    // context despite a strong match. The budget still bounds every subsequent chunk.
    if (documentContext && totalTokens + chunkTokens > maxTokens * 0.7) break; // Leave room for history

    if (documentContext) documentContext += '\n\n---\n\n';
    documentContext += `[${doc.metadata?.filename || 'Document'}`;
    if (doc.metadata?.pageNumber) {
      documentContext += ` - Page ${doc.metadata.pageNumber}`;
    }
    if (doc.metadata?.sectionTitle) {
      documentContext += ` - ${doc.metadata.sectionTitle}`;
    }
    documentContext += `]\n${doc.chunk_text}`;

    sources.push({
      type: 'document',
      label: doc.metadata?.filename || 'Document',
      snippet: truncateText(doc.chunk_text, 100),
      referenceId: doc.document_id,
      location: doc.metadata?.pageNumber
        ? `Page ${doc.metadata.pageNumber}`
        : doc.metadata?.sectionTitle,
      relevance: doc.similarity,
    });

    totalTokens += chunkTokens;
  }

  // Add history (remaining budget)
  const sortedHistory = [...historyResults].sort(
    (a, b) => b.similarity - a.similarity
  );

  for (const msg of sortedHistory) {
    const msgTokens = estimateTokens(msg.content);
    if (totalTokens + msgTokens > maxTokens) break;

    if (historyContext) historyContext += '\n';
    historyContext += `[${msg.role === 'user' ? 'You' : 'Assistant'} - ${formatDate(msg.created_at)}]: ${msg.content}`;

    sources.push({
      type: 'history',
      label: 'Previous conversation',
      snippet: truncateText(msg.content, 100),
      referenceId: msg.conversation_id,
      relevance: msg.similarity,
    });

    totalTokens += msgTokens;
  }

  return {
    documentContext,
    historyContext,
    sources,
    totalTokens,
  };
}

// ============================================
// Utilities
// ============================================

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
